import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { RouteConfig, RouteType } from '@shared/components/shared.model';
import { ConstantsService } from 'app/dp-world/services/constants.service';
import { LoadExternalScriptService } from 'app/dp-world/services/load-external-script.service';
import { LogService } from 'app/dp-world/services/log.service';
import { NavigationConfigService } from 'app/dp-world/services/navigation-config.service';
import { UiService } from 'app/dp-world/services/ui.service';
import { UtilsService } from 'app/dp-world/services/utils.service';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { GoogleAnalyticalService } from './../../../dp-world/services/google-analytics.service';
import { AuthData } from './auth-data.model';
import { LoginResponse, User } from './user.model';
import { ProductConfigService } from 'app/dp-world/services/product-config.service';
declare let moment: any;

/**
 * handles a bunch of stuff around login.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly pendingActionStorageKey = environment.storage.pendingAction;
  public currentUserSubject: BehaviorSubject<User> = new BehaviorSubject<User>(null);
  public readonly currentUser: Observable<User> = this.currentUserSubject.asObservable();
  public uploadedDocSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public readonly uploadedDoc: Observable<User> = this.uploadedDocSubject.asObservable();
  public isOpenCustomerDisclaimer: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public latestValueSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public downloadSubject: Subject<any> = new Subject();
  public user: User;
  public token: string;
  public idleCount: number;
  public refreshDataCount: number;
  public idleTimer;
  // used by Dubai Trade integration to hide side-login component.
  public isDubaiTradeUser = Boolean(false);
  public allFinances: any[] = [];
  public mawaniAllowedFinanceTypes: any[] = [];
  public tenantCodes = [environment.serviceProviderCode, environment.serviceProviderCodeCayman];
  public spinnerSubject: Subject<boolean> = new Subject();
  public isClientSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  /** Tracks whether the user has pending action that should block route navigation. */
  public pendingActionSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public pendingStatuses = ['Pending Approval', 'Partially Approved', 'On Hold'];
  public loginProgress: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  public pendingStatusesWithNoUpdateAllowed = [
    'Pending Approval',
    'Partially Approved',
    'Amendment Pending',
  ];
  public pendingOrgApprovalStatuses = [
    'Draft',
    'Pending Approval',
    'Partially Approved',
    'On Hold',
  ];
  lendingUrlLogin: string;
  params: ParamMap;
  routeConfigPostProcessing: RouteConfig;
  /** Emits true once the navigation config has been fetched and stored in localStorage. */
  routeConfigReady$ = new BehaviorSubject<boolean>(false);

  constructor(
    private router: Router,
    private http: HttpClient,
    private ui: UiService,
    private utils: UtilsService,
    private matDialog: MatDialog,
    private logger: LogService,
    private googleAnalytics: GoogleAnalyticalService,
    private constants: ConstantsService,
    private route: ActivatedRoute,
    private loadExternalScriptService: LoadExternalScriptService,
    private prodConfig: ProductConfigService,
    private navigationConfigService: NavigationConfigService
  ) {
    if (!(window.location.host === 'tradefinance.dpworld.com' || window.location.host === 'qa-tradefinance.dpworld.com')) {
      const scripts = ['moment'];
      if (environment.environmentName.toLowerCase() !== 'dev') {
        let newRelicScriptName = 'new-relic-staging';
        if (environment.environmentName.toLowerCase() === 'prod') {
          newRelicScriptName = 'new-relic-prod';
        } else if (environment.environmentName.toLowerCase() === 'demo' || environment.environmentName.toLowerCase() === 'uat') {
          newRelicScriptName = 'new-relic-demo';
        }
        scripts.push(newRelicScriptName, 'gtm');
      }
      this.loadExternalScriptService.load(...scripts);
    }

    this.retrieveCurrentUser();
    const retrieveData = localStorage.getItem(environment.storage.routeConfig);
    if (this.user && retrieveData) {
      // Page reload with a cached config — mark ready immediately so any
      // consumers of routeConfigReady$ unblock without waiting for a fetch.
      // The layout will re-fetch per-platform when setSideBarContent runs.
      this.routeConfigReady$.next(true);
    }
  }

  /**
   * Fetches navigation config for the given platform, stores it in
   * localStorage, and marks routeConfigReady$ as true.
   *
   * @param platform - empty string for the main/default menu; will expand to
   *                   'scf' | 'bhs' | 'accounting' once BE params are finalised.
   */
  fetchRouteConfig(platform = ''): Promise<void> {
    const cachedPlatform = localStorage.getItem(environment.storage.routeConfigPlatform);
    const cachedConfig = localStorage.getItem(environment.storage.routeConfig);
    if (cachedPlatform === platform && cachedConfig) {
      // this.routeConfigReady$.next(true);
      return Promise.resolve();
    }

    return this.navigationConfigService.fetchAndStore(platform)
      .then(() => this.routeConfigReady$.next(true))
      .catch(err => {
        console.error('Failed to fetch navigation config', err);
        this.routeConfigReady$.next(true); // allow app to continue with empty config
      });
  }

  private retrieveCurrentUser() {
    const user = JSON.parse(localStorage.getItem(environment.storage.currentUser));

    if (user == null) {
      return;
    }

    this.idleCount = 0;
    this.refreshDataCount = 0;
    this.startIdleTimer();

    const hours = 3600000;
    const original = user.timestamp;
    const now = new Date().getTime();
    const diff = now - original;

    // token still valid
    if (diff < hours) {
      this.handleNewUser(user);

      // token expired
    } else {
      console.log('login is too old, removing! ' + diff);
      localStorage.removeItem(environment.storage.currentUser);
      localStorage.removeItem(environment.storage.companyName);
      localStorage.removeItem(environment.storage.annualTransaction);
      localStorage.removeItem(environment.storage.currentTenant);
      localStorage.removeItem(environment.storage.switchTenant);
      localStorage.removeItem(environment.storage.onboardingClientCountry);
      localStorage.removeItem(environment.storage.clientOrFI);
      localStorage.removeItem(environment.storage.addCompClientOrFI);
      localStorage.removeItem(environment.storage.productCombination);
      localStorage.removeItem(environment.storage.registerCompanyAccessed);
      localStorage.removeItem(environment.storage.termsAndConditionsAccepted);
      localStorage.removeItem(environment.storage.clientOrFiAgreementsAccepted);
      localStorage.removeItem(environment.storage.returnToHomeDueToFailure);
      localStorage.removeItem(environment.storage.FinancialInstitution);
      localStorage.removeItem(environment.storage.lendingEntityExists);
      localStorage.removeItem(environment.storage.termsAndConditionsAcceptedArranging);
      localStorage.removeItem(environment.storage.kycRenewalFilters);
      localStorage.removeItem(environment.storage.ongoingMonitoringFilters);
      localStorage.removeItem(environment.storage.applicationFilters);
      localStorage.removeItem(environment.storage.creditFacilityFilters);
      localStorage.removeItem(environment.storage.kycDocRenewalFilters);
      localStorage.removeItem(environment.storage.getLatestReleaseNotes);
      localStorage.removeItem(environment.storage.releaseNotesNotificationPopup);
      localStorage.removeItem(environment.storage.scfPlatform);
      localStorage.removeItem(environment.storage.accountingReconciliationModule);
      localStorage.removeItem(environment.storage.routeConfig);
      localStorage.removeItem(environment.storage.routeConfigPlatform);
      localStorage.removeItem(environment.storage.bhsModule);
      localStorage.removeItem(environment.storage.pendingAction);
      localStorage.removeItem(`${environment.storage.clientClassificationNoticeForFacility}-DTF`);
      localStorage.removeItem(`${environment.storage.clientClassificationNoticeForFacility}-DTFCM`);
      localStorage.removeItem(`${environment.storage.clientClassificationNotice}-DTF`);
      localStorage.removeItem(`${environment.storage.clientClassificationNotice}-DTFCM`);
      localStorage.removeItem(`${environment.storage.clientClassificationNoticeForFI}-DTF`);
      localStorage.removeItem(`${environment.storage.clientClassificationNoticeForFI}-DTFCM`);
    }
  }

  setPendingAction(value: boolean): void {
    this.pendingActionSubject.next(value);
    if (value) {
      localStorage.setItem(this.pendingActionStorageKey, 'true');
      return;
    }
    localStorage.removeItem(this.pendingActionStorageKey);
  }

  hasPendingAction(): boolean {
    return this.pendingActionSubject.value || localStorage.getItem(this.pendingActionStorageKey) === 'true';
  }

  private handleNewUser(user: User) {
    this.user = user;
    if (user) {
      // moment.locale(user.locale);
    }
    if (this.user.user_roles) {
      const userRoles = {};
      this.user.user_roles.forEach(instance => {
        userRoles[instance] = true;
      });
      this.user.authorities = userRoles;
      this.validateAllowedFinanceTypes();
    }
    if (this.user.tenant_code || this.user.metadata?.last_logged_in_tenant) {
      const tenantCode = this.user.metadata?.last_logged_in_tenant
        ? this.user.metadata.last_logged_in_tenant
        : this.user.tenant_code;
      this.setTenantCode(tenantCode);
    }
    // else {
    //   this.router.navigate(['/sessions/403']);
    // }
    if (this.user.is_lending_organisation && !this.isLoggedInToAccountingModule()) {
      localStorage.setItem(environment.storage.lendingEntityExists, 'true');
      this.utils.setFiAssessedState(true);
    }
    this.currentUserSubject.next(user);
    if (this.user.organisation_id && this.user.organisation_type !== 'ORG') {
      const userType = this.user.organisation_type === 'BANK' ? 'Financial Institution' : 'Admin';
      this.googleAnalytics.pushTag({ 'userType': userType });
    }
  }

  public get currentUserValue(): User {
    return this.user;
    // return this.currentUserSubject.value;
  }

  public get uploadDocValue(): User {
    return this.uploadedDocSubject.value;
  }

  //TODO: clean @Param { routeToCheck }
  checkAndValidateRoutePrivilege(routeToCheck: string, orgType: string): boolean {
    if (orgType === 'DTF') {
      return true;
    }
    const tenantCode = this.getTenantCode();
    if (this.utils.isCaymanOrgRequestPending(this.user, tenantCode)) {
      return true;
    }
    const routeConfig: RouteConfig = this.getRouteConfig();
    const routerInstances: RouteType[] = routeConfig.routerList.filter(
      (routerInstance: RouteType) => routeToCheck.includes(routerInstance.route)
    ); //TODO: can be replace with ===

    if (!(routerInstances && routerInstances.length > 0)) {
      return false;
    }

    let isHasPrivilegeToAccess = false;

    const routerInstance = routerInstances[0];
    if (!routerInstance.privilegeRequired) {
      isHasPrivilegeToAccess = true;
    } else if (this.allFinances.length === 0) {
      isHasPrivilegeToAccess = false;
    } else if (routerInstance.privileges) {
      for (const privilege of routerInstance.privileges) {
        if (this.user.authorities[privilege]) {
          isHasPrivilegeToAccess = true;
          break;
        }
      }
    }
    return isHasPrivilegeToAccess;
  }

  login(authData: AuthData) {
    return this.http
      .post(
        environment.rootUrl + environment.urls.login,
        {},
        {
          headers: new HttpHeaders({
            [environment.apiKey]: environment.apiValue,
            Authorization: 'Basic ' + btoa(authData.username + ':' + authData.password),
          }),
          observe: 'response',
        }
      )
      .pipe(
        map((response: HttpResponse<LoginResponse>) => {
          //console.log("login; response: ", response);
          //console.log("login; response.headers: ", response.headers);

          return this.handleLoginSuccess(response);
        })
      );
  }

  loginToCargoesFinance(loginParams: { code: string; redirect_uri: string }) {
    return this.http
      .post(environment.rootUrl + environment.urls.login, loginParams, {
        headers: new HttpHeaders({
          [environment.apiKey]: environment.apiValue,
        }),
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<LoginResponse>) => {
          return this.handleLoginSuccess(response)
        })
      );
  }

  /**
   * used by Dubai Trade integration
   * @param token - token
   */
  loginWithToken(token: string): Observable<User> {
    return this.http
      .get(environment.rootUrl + environment.urls.loginWithToken, {
        headers: new HttpHeaders({
          [environment.apiKey]: environment.apiValue,
          [environment.HEADER.X_USER_TOKEN]: token,
        }),
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<any>) => {
          return this.handleLoginSuccess(response);
        })
      );
  }

  handleLoginSuccess(response, calledFrom?: any): User {
    const user = response.body.userData as User;

    if (calledFrom !== 'bhsModule') {
      user.token = response.headers.get('x-dpw-user-token');
      const refresh = response.body.refreshToken;
      user.refreshToken = refresh;
    }
    user.timestamp = moment().toDate().getTime();

    if (response.body.company_name) {
      localStorage.setItem(environment.storage.companyName, response.body.company_name);
    }
    if (response.body.annual_turnover) {
      localStorage.setItem(environment.storage.annualTransaction, response.body.annual_turnover);
    }
    localStorage.setItem(environment.storage.currentUser, JSON.stringify(user));
    this.handleNewUser(user);
    return user;
  }

  verifyCaptcha(captchaToken: string) {
    return this.http.post(environment.rootUrl + environment.urls.verify_captcha, {
      captcha_token: captchaToken,
    });
  }

  private authSuccessfully() {
    // this.authChange.next(true);
    this.router.navigate(['/welcome']);
  }

  logout() {
    this.ui.snackbar('You have been logged out!');
    localStorage.removeItem(environment.storage.currentUser);
    localStorage.removeItem(environment.storage.companyName);
    localStorage.removeItem(environment.storage.annualTransaction);
    localStorage.removeItem(environment.storage.currentTenant);
    localStorage.removeItem(environment.storage.switchTenant);
    localStorage.removeItem(environment.storage.onboardingClientCountry);
    localStorage.removeItem(environment.storage.clientOrFI);
    localStorage.removeItem(environment.storage.addCompClientOrFI);
    localStorage.removeItem(environment.storage.productCombination);
    localStorage.removeItem(environment.storage.registerCompanyAccessed);
    localStorage.removeItem(environment.storage.termsAndConditionsAccepted);
    localStorage.removeItem(environment.storage.clientOrFiAgreementsAccepted);
    localStorage.removeItem(environment.storage.returnToHomeDueToFailure);
    localStorage.removeItem(environment.storage.FinancialInstitution);
    localStorage.removeItem(environment.storage.lendingEntityExists);
    localStorage.removeItem(environment.storage.termsAndConditionsAcceptedArranging);
    localStorage.removeItem(environment.storage.kycRenewalFilters);
    localStorage.removeItem(environment.storage.ongoingMonitoringFilters);
    localStorage.removeItem(environment.storage.applicationFilters);
    localStorage.removeItem(environment.storage.creditFacilityFilters);
    localStorage.removeItem(environment.storage.kycDocRenewalFilters);
    localStorage.removeItem(environment.storage.getLatestReleaseNotes);
    localStorage.removeItem(environment.storage.releaseNotesNotificationPopup);
    localStorage.removeItem(environment.storage.scfPlatform);
    localStorage.removeItem(environment.storage.accountingReconciliationModule);
    localStorage.removeItem(environment.storage.routeConfig);
    localStorage.removeItem(environment.storage.routeConfigPlatform);
    this.routeConfigReady$.next(false);
    localStorage.removeItem(environment.storage.bhsModule);
    localStorage.removeItem(environment.storage.pendingAction);
    localStorage.removeItem(`${environment.storage.clientClassificationNoticeForFacility}-DTF`);
    localStorage.removeItem(`${environment.storage.clientClassificationNoticeForFacility}-DTFCM`);
    localStorage.removeItem(`${environment.storage.clientClassificationNotice}-DTF`);
    localStorage.removeItem(`${environment.storage.clientClassificationNotice}-DTFCM`);
    localStorage.removeItem(`${environment.storage.clientClassificationNoticeForFI}-DTF`);
    localStorage.removeItem(`${environment.storage.clientClassificationNoticeForFI}-DTFCM`);
    this.user = null;
    this.currentUserSubject.next(null);
    this.matDialog.closeAll();

    console.log('logout; this.currentUserValue: ', this.currentUserValue);
    this.navigateToClientRegistration('logout');
    // this.router.navigate(['/home/def']);
  }

  isAuth() {
    if (
      this.currentUserValue != null &&
      this.currentUserValue.is_org_verified &&
      this.currentUserValue.is_verified
    ) {
      return true;
    }
    const tenantCode = this.getTenantCode();
    return this.utils.isCaymanOrgRequestPending(this.currentUserValue, tenantCode);
  }

  isOnlyVerified(): boolean {
    return this.currentUserValue != null && this.currentUserValue.is_verified;
  }

  isTermsAccepted(): boolean {
    const tenantCode = this.getTenantCode();
    const termsAcceptedStr = localStorage.getItem(environment.storage.termsAndConditionsAccepted);
    const termsAcceptedObj = termsAcceptedStr ? JSON.parse(termsAcceptedStr) : '';
    const isTermAndConditionsAlreadyAccepted =
      termsAcceptedObj && termsAcceptedObj[tenantCode] ? true : false;
    return (
      isTermAndConditionsAlreadyAccepted ||
      (this.user.accepted_terms_version[tenantCode].TermsAndCondition &&
        this.user.accepted_terms_version[tenantCode].TermsAndCondition.length > 0)
    );
  }

  storeVersionOfDocumentAccordingUser(version: any, headers?: any): Observable<any> {
    const url = environment.rootUrl + environment.urls.store_document_acceptance_version;
    return this.http.post<any>(url, version, { headers });
  }

  isAdmin() {
    return this.currentUserValue && this.currentUserValue.isAdmin;
  }

  getUserLocale() {
    const user = this.currentUserValue;
    const allowedLocales = ['en-US', 'en-GB'];
    if (user && user.locale && allowedLocales.indexOf(user.locale) !== -1) {
      return user.locale;
    }
    // console.warn('check user locale for its validation');
    return 'en-US';
  }

  // validateTokenExpiry(user: User): boolean {
  //   const hours = 3600000;
  //   const original = user.timestamp;
  //   const now = new Date().getTime();
  //   const diff = now - original;
  //   // Not Expired: false, Expired: true
  //   return diff < hours ? false : true;
  // }

  // validateTokenExpiryViaAPI(): Observable<any> {
  //   const url = environment.rootUrl + environment.urls.validateAccessToken;
  //   return this.http.get(url, { responseType: 'text' });
  // }

  validateUserAgreements(tenantCode: string): boolean {
    if (
      this.user.accepted_terms_version[tenantCode]?.TermsAndCondition?.length > 0
    ) {
      if (this.user.organisation_type === 'BANK') {
        if (
          tenantCode === environment.serviceProviderCodeCayman &&
          this.user.companyStatus &&
          this.pendingOrgApprovalStatuses.includes(this.user.companyStatus.name)
        ) {
          return true;
        } else if (
          this.user.accepted_terms_version[tenantCode].FIStandardTerms &&
          this.user.accepted_terms_version[tenantCode].FIStandardTerms.length > 0
        ) {
          return true;
        }
      }
      if (this.user.organisation_type === 'ORG') {
        if (
          tenantCode === environment.serviceProviderCodeCayman &&
          this.user.companyStatus &&
          this.pendingOrgApprovalStatuses.includes(this.user.companyStatus.name)
        ) {
          return true;
        } else if (
          this.user.accepted_terms_version[tenantCode].ClientAgreement &&
          this.user.accepted_terms_version[tenantCode].ClientAgreement.length > 0 &&
          (localStorage.getItem(environment.storage.clientOrFiAgreementsAccepted) ||
            !this.isClientAgreementPending())
        ) {
          return true;
        }
      }
    }
    return false;
  }

  isClientAgreementPending(): boolean {
    let isPending = true;
    const tenantCode = this.getTenantCode();
    const clientAgreements = this.user.accepted_terms_version[tenantCode]?.ClientAgreement;
    for (let i = 0; i < clientAgreements?.length; i++) {
      if (!clientAgreements[i].termsAccepted) {
        return true;
      } else {
        isPending = false;
      }
    }
    return isPending;
  }

  startIdleTimer(): void {
    this.idleTimer = setInterval(() => {
      this.idleCount++;
      this.refreshDataCount++;
      console.log('refreshDataCount: ', this.refreshDataCount);
      console.log('idleCount: ', this.idleCount);
      // Refresh user data and refresh token every 10 minutes
      // Access token validity: 15 minutes, Refresh token validity: 30 minutes
      if (this.refreshDataCount % 10 === 0 && this.idleCount < 25) {
        if (this.router.url.includes('acc-rec')) {
          this.refreshUserData('accountingModule');
        } else if (this.router.url.includes('bhs')) {
          this.refreshUserData('bhsModule');
        } else {
          this.refreshUserData();
        }
      } else if (this.idleCount >= 25) {
        // Logout after 25 minutes of inactivity to ensure logout happens before refresh token expires (30 minutes)
        this.clearIntervalAndLogout();
      }
      this.updateUserActionDetections('add');
    }, 1000 * 60); // 1000 * 60 = 60 seconds
  }

  updateUserActionDetections(actionState: string): void {
    // Below events will be helpful to reset the counter, in case user performs any on-screen actions
    window.onload = () => {
      if (actionState === 'add') {
        console.log('onload, action add called');
        this.resetIdleTimer();
      }
    };
    const userActionOnDocument = [
      'onmousemove',
      'onkeypress',
      'onload',
      'onmousedown',
      'ontouchstart',
      'onclick',
      'onscroll',
    ];
    userActionOnDocument.forEach(action => {
      document[action] = () => {
        if (actionState === 'add') {
          console.log(action + ', action add called');
          this.resetIdleTimer();
        }
      };
    });
  }

  resetIdleTimer(): void {
    console.log('reset Called');
    clearInterval(this.idleTimer);
    this.idleCount = 0;
    this.updateUserActionDetections('remove');
    if (this.user) {
      this.startIdleTimer();
    }
  }

  stopIdleTimer(): void {
    clearInterval(this.idleTimer);
  }

  private clearIntervalAndLogout(): void {
    clearInterval(this.idleTimer);
    this.idleCount = 0;
    this.refreshDataCount = 0;
    console.log('logged out');
    this.updateUserActionDetections('remove');
    this.logout();
    setTimeout(() => {
      this.ui.closeCurrentSnackbar();
      this.ui.snackbar('Your session timed out due to inactivity. Please log in again.', 5000);
    }, 0);
    if (this.router.url.includes('home/def') && window.location.host !== 'localhost:9876') {
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    }
  }

  getDocumentsFromPublicApi(docType: string, format: string): Observable<any> {
    const headers = new HttpHeaders({
      'X-Tenant-Code': this.getTenantCode()
    });
    const url =
      environment.rootUrl +
      environment.urls.publicDocumentApi.replace('docType', docType).replace('format', format);
    return this.http.get(url, { headers, responseType: 'text' });
  }

  validateAllowedFinanceTypes(): void {
    let mawaniAllowedFinanceTypes: any[] = [];
    let allFinances: any[] = JSON.parse(JSON.stringify(this.prodConfig.allFinances));
    if (this.user.organisation_type !== 'DTF' || (this.user.organisation_type === 'DTF' && this.user?.metadata?.last_finance_type && this.user?.metadata?.last_product_type)) {
      mawaniAllowedFinanceTypes = this.getFinTypeAccToMawaniAllowedServices();
      if (mawaniAllowedFinanceTypes.length) {
        const perUserPrivilegeFinTypes = [];
        mawaniAllowedFinanceTypes.forEach((finType: string) => {
          this.prodConfig.userRoleFinTypeDesciption[finType]?.forEach((finTypeShortHand: string) => {
            if (this.user.authorities[finTypeShortHand]) {
              const index = this.findFinancesIndex(finType, allFinances);
              if (!allFinances[index]['allowed'] && !perUserPrivilegeFinTypes.includes(finType)) {
                allFinances[index]['allowed'] = true;
                perUserPrivilegeFinTypes.push(finType);
              }
            }
          });
        });
        mawaniAllowedFinanceTypes = perUserPrivilegeFinTypes;
        allFinances = allFinances.filter(item => {
          if (item.hasOwnProperty('allowed')) {
            delete item['allowed'];
            return true;
          }
          return false;
        });
      }
    }
    this.setAllowedFinances(allFinances, mawaniAllowedFinanceTypes);
  }

  setAllowedFinances(allFinances: any[], mawaniAllowedFinaceTypes: any[]) {
    this.allFinances = allFinances;
    this.mawaniAllowedFinanceTypes = mawaniAllowedFinaceTypes;
  }

  getAllowedFinances() {
    return {
      allFinances: this.allFinances,
      mawaniAllowedFinanceTypes: this.mawaniAllowedFinanceTypes,
    };
  }

  private getFinTypeAccToMawaniAllowedServices(): any[] {
    if (this.user.allowed_services) {
      return Object.keys(this.user.allowed_services).filter(
        item => this.user.allowed_services[item]
      );
    }
    return [];
  }

  private findFinancesIndex(finType: string, allFinances: any[]): number {
    return allFinances.findIndex((singleFinance) =>
      this.prodConfig.combineProductCategoryAndProductType(finType, finType) === this.prodConfig.combineProductCategoryAndProductType(singleFinance.viewValue, singleFinance.viewValue)
    );
  }

  navigateToClientRegistration(calledFrom?: string): void {
    let encodedQueryParams = '';
    if (calledFrom !== 'logout') {
      let queryParamString = '';
      // Remove demo once backend changes are done
      if (this.lendingUrlLogin) {
        queryParamString = this.lendingUrlLogin;
      } else if (Object.keys(this.route.snapshot.queryParams).length) {
        queryParamString =
          typeof this.route.snapshot.queryParams === 'string'
            ? this.route.snapshot.queryParams
            : JSON.stringify(this.route.snapshot.queryParams);
      }
      encodedQueryParams = queryParamString ? queryParamString : '/dashboard';
    }
    const urlReqQueryParams = {
      state: btoa(encodedQueryParams),
      client_id: environment.clientId,
      response_type: 'code',
      redirect_uri: environment.clientRegistrationRedirectionURL + 'home/def',
    };
    console.log('urlReqQueryParams.state: ', urlReqQueryParams.state);
    const navigationURL =
      environment.clientRegistrationURL +
      (calledFrom === 'logout'
        ? `logout?post_logout_redirect_uri=${urlReqQueryParams.redirect_uri}&client_id=CF`
        : `auth?state=${urlReqQueryParams.state}&client_id=${urlReqQueryParams.client_id}&response_type=code&redirect_uri=${urlReqQueryParams.redirect_uri}`);
    if (!window.location.href.includes('localhost:9876')) {
      window.open(navigationURL, '_self');
    }
  }

  openMawaniCompanySearch(goTo: string): void {
    const isDirectLoginViaUserSignup = localStorage.getItem(environment.storage.companyName);
    if (goTo === 'company-search' && isDirectLoginViaUserSignup) {
      const isRegisterBtnAlreadyUsed = localStorage.getItem(environment.storage.registerCompanyAccessed);
      if (!isRegisterBtnAlreadyUsed) {
        window.open(environment.mawaniURL + 'login/' + environment.clientId + '?session_state=' + this.user.refreshToken);
        localStorage.setItem(environment.storage.registerCompanyAccessed, 'true');
      } else {
        window.open(environment.mawaniURL + 'company-search').focus();
      }
    } else {
      const tenantCode = this.getTenantCode();
      const navigationUrl = `${environment.mawaniURL}redirect/redirection/CF?redirect_url=${environment.mawaniURL}${goTo}&tenantCode=CF&X-COMPANY-REFERENCE=${this.user.mawani_org_reference_id}&X-SERVICE-PROVIDER-CODE=${tenantCode}&X-CLIENT-TYPE-CODE=${this.user.client_type_code}`;
      window.open(navigationUrl);
    }
  }

  switchOrganisationForUser(companyReferenceId: string): Observable<User> {
    const url = environment.rootUrl + environment.urls.switch_orgaisation;
    const params = {
      companyReferenceId: companyReferenceId
    };
    if (this.user.refreshToken) {
      params['refreshToken'] = this.user.refreshToken;
    }
    return this.http
      .post(url, params, { observe: 'response' })
      .pipe(
        map((response: HttpResponse<any>) => {
          return this.handleLoginSuccess(response);
        })
      );
  }

  setTenantCode(tenant: string): void {
    localStorage.setItem(environment.storage.currentTenant, tenant);
  }

  getTenantCode(): string {
    return localStorage.getItem(environment.storage.currentTenant);
  }

  getCountries(type: string): Observable<any> {
    const url = environment.mawaniReferentialApiURL + type;
    return this.http.get<any>(url);
  }

  switchTenant(): Observable<any> {
    const url = environment.rootUrl + environment.urls.switchTenantUrl;
    return this.http.post(url, {});
  }

  triggerTenantSwitch(tenant: string): void {
    const initalTenant =
      tenant === environment.serviceProviderCode
        ? environment.serviceProviderCodeCayman
        : environment.serviceProviderCode;
    this.setTenantCode(initalTenant);
    this.spinnerSubject.next(true);
    this.switchTenant().subscribe(
      response => {
        if (!response) {
          this.spinnerSubject.next(false);
          const errMsg = `Unable to fetch ${tenant} information from backend.`;
          this.ui.snackbar(errMsg);
          this.logger.logError(errMsg);
          return;
        }
        if (response.companyReference) {
          this.triggerOrgSwitch(response.companyReference);
        }
      },
      error => {
        this.spinnerSubject.next(false);
        const errorMessage =
          error && error.error && error.error.error ? error.error.error : error.error;
        const errMsg =
          typeof errorMessage === 'string'
            ? errorMessage
            : `You are not a registered user of the Organization on this platform. Please contact your Admin.`;
        this.ui.snackbar(errMsg);
        this.logger.logError(errMsg);
      }
    );
  }

  triggerOrgSwitch(companyReference, calledFrom?: string, redirectionUrl?: string): void {
    this.switchOrganisationForUser(companyReference).subscribe(
      user => {
        this.spinnerSubject.next(false);
        if (!user) {
          this.ui.snackbar('Unable to fetch user information from backend.');
          if (calledFrom === 'notificationUrlSwitch') {
            this.logout();
          }
          return;
        }
        localStorage.removeItem(environment.storage.switchTenant);
        if (!user.is_lending_organisation) {
          localStorage.removeItem(environment.storage.lendingEntityExists);
          this.setTenantCode(user.tenant_code);
        }
        if (calledFrom === 'caymanRegSubmit') {
          this.ui.snackbar('Company successfully created.');
        } else if (calledFrom === 'caymanRegUpdate') {
          this.ui.snackbar('Company successfully updated.');
        } else if (calledFrom === 'notificationUrlSwitch') {
          this.ui.snackbar('Tenant switch is in progress, please be patient.');
        } else {
          this.ui.snackbar('Tenant successfully switched for the user.');
        }
        if (calledFrom !== 'encodedMawaniId') {
          setTimeout(() => {
            this.router.navigate(['home/def']).then(() => {
              localStorage.removeItem(environment.storage.routeConfig);
              if (window.location.host !== 'localhost:9876') {
                window.location.reload();
              }
            });
          }, 2000);
        } else {
          window.location.href = redirectionUrl;
        }
      },
      error => {
        localStorage.removeItem(environment.storage.switchTenant);
        this.spinnerSubject.next(false);
        if (calledFrom === 'notificationUrlSwitch') {
          this.logout();
        } else {
          this.logger.logError('Unable to fetch user information from backend.');
          this.ui.snackbar('Unable to fetch user information from backend. Please reload!');
        }
      }
    );
  }

  setSwitchedTenant(tenantCode: string): void {
    localStorage.setItem(environment.storage.switchTenant, tenantCode);
  }

  getSwitchedTenant(): string {
    const tenantCode = localStorage.getItem(environment.storage.switchTenant);
    return tenantCode ? tenantCode : '';
  }

  getUserSignUpCompany(): string {
    const companyName = localStorage.getItem(environment.storage.companyName);
    return companyName ? companyName : '';
  }

  getCompanySearchData(params): Observable<any> {
    const url = environment.rootUrl + environment.urls.panjivaCompaniesUrl;
    return this.http.get(url, { params });
  }

  getEditCompaniesList(params): Observable<any> {
    const url = environment.rootUrl + environment.urls.getMawaniCompanyInDraftUrl;
    return this.http.get(url, { params, observe: 'response' });
  }
  storeVersionOfDocumentAccordingUserForLendingOrg(version: any): Observable<any> {
    const url = environment.rootUrl + environment.urls.store_document_acceptance_version_lending;
    return this.http.post<any>(url, version);
  }

  setLendingLoginUrl(lendingUrlLogin: string): void {
    this.lendingUrlLogin = lendingUrlLogin;
  }

  refreshUserData(calledFrom?: string): void {
    const rootUrl = calledFrom === 'accountingModule' ? environment.accountingModuleRootApiUrl : (calledFrom === 'bhsModule' ? environment.bhsModuleRootUrl : environment.rootUrl);
    const url = rootUrl + environment.urls.refreshTokenUrl;
    const headers = new HttpHeaders({
      [environment.HEADER.X_USER_REFRESH_TOKEN]: this.user.refreshToken,
      [environment.HEADER.X_USER_TOKEN]: this.user.token
    });
    const tenantCode = this.getTenantCode();
    if (tenantCode) {
      headers[environment.HEADER.X_TENANT_CODE] = tenantCode;
    }
    this.http
      .post(
        url,
        {},
        {
          headers,
          observe: 'response',
        }
      )
      .pipe(
        map((response: HttpResponse<LoginResponse>) => {
          if (calledFrom === 'bhsModule') {
            response.body.userData = response.body.data.user_data;
            // response.body.userData['refreshToken'] = response.body.data['refreshToken'];
            response.body.userData['refreshToken'] = response.body.userData['refresh_token'];
            response.body.userData['token'] = response.body.data['accessToken'];
          }

          if (calledFrom === 'accountingModule' || calledFrom === 'bhsModule') {
            response.body.userData['accepted_terms_version'] = this.user.accepted_terms_version;
            response.body.userData['latest_terms_version'] = this.user.latest_terms_version;
          }
          return this.handleLoginSuccess(response, calledFrom);
        })
      )
      .subscribe(
        response => {
          if (response && response['refreshToken']) {
            this.refreshDataCount = 0;
            if (calledFrom === 'dp-world-header') {
              this.spinnerSubject.next(false);
              window.location.href = '/dashboard';
            } else if (calledFrom === 'accountingModule') {
              this.spinnerSubject.next(false);
              localStorage.removeItem(environment.storage.lendingEntityExists);
              localStorage.setItem(environment.storage.accountingReconciliationModule, 'true');
              window.location.href = '/acc-rec';
            } else if (calledFrom === 'bhsModule') {
              this.spinnerSubject.next(false);
              localStorage.removeItem(environment.storage.accountingReconciliationModule);
              localStorage.setItem(environment.storage.bhsModule, 'true');
              if (!window.location.href.includes('bhs')) {
                window.location.href = '/bhs';
              }
            }
          }
        },
        error => {
          this.spinnerSubject.next(false);
          console.log('Error in refreshing the user data ', error);
        }
      );
  }

  getRouteConfig(): RouteConfig {
    const retrieveData = localStorage.getItem(environment.storage.routeConfig);
    const routeConfig = retrieveData ? JSON.parse(retrieveData) : {
      sidebarOptions: [],
      scfSidebarOptions: [],
      routerList: [],
      accountingModuleSidebarOptions: [],
      buyHoldSellOptions: [],
    };
    return routeConfig;
  }

  setRouteParams(params: ParamMap): void {
    this.params = params;
  }

  getRouteParams(): ParamMap {
    return this.params;
  }

  setProcessedRouteConfig(processedConfig: RouteConfig) {
    this.routeConfigPostProcessing = processedConfig;
  }

  validateAccountingModuleAccess(checkForAmendAccessOnly?: boolean): boolean {
    const user: User = this.currentUserValue;
    if (user?.authorities) {
      const accountingReconciliationRoles = environment.USER_ROLES.ACCOUNTING_RECONCILIATION;
      let hasPrivilege = false;
      for (let role in accountingReconciliationRoles) {
        if (checkForAmendAccessOnly) {
          if (user.authorities[accountingReconciliationRoles[role].AMEND]) {
            hasPrivilege = true;
            break;
          }
        } else {
          if (user.authorities[accountingReconciliationRoles[role].AMEND] || user.authorities[accountingReconciliationRoles[role].VIEW]) {
            hasPrivilege = true;
            break;
          }
        }
      }
      return hasPrivilege;
    }
    return false;
  }

  validateBhsModuleAccess(): boolean {
    const user: User = this.currentUserValue;
    if (user?.authorities) {
      const BhsRoles = environment.USER_ROLES.BHS;
      let hasPrivilege = false;
      for (let role in BhsRoles) {
        if (user.authorities[BhsRoles[role].AMEND] || user.authorities[BhsRoles[role].VIEW]) {
          hasPrivilege = true;
          break;
        }
      }
      return hasPrivilege;
    }
    return false;
  }

  isLoggedInToAccountingModule(): boolean {
    return localStorage.getItem(environment.storage.accountingReconciliationModule) === 'true' && this.validateAccountingModuleAccess() ? true : false;
  }

  isLoggedInToBHSModule(): boolean {
    return localStorage.getItem(environment.storage.bhsModule) === 'true' ? true : false;
  }

  getProductDefinitions(user: User): any[] {
    return this.prodConfig.allProductDefinitions.filter((product) => {
      return this.isAllowedByMawani(product.mawaniRefKey, user) && !this.isReqCreditBtnVisible(product.category, product.productType, user);
    });
  }

  isAllowedByMawani(productType: string, user: User): boolean {
    if (user && user.allowed_services[productType]) {
      for (const finTypeShortHand of this.prodConfig.userRoleFinTypeDesciption[productType]) {
        if (user.authorities && user.authorities[finTypeShortHand]) {
          return true;
        }
      }
    }
    return false;
  }

  isReqCreditBtnVisible(productCategory: string, productType: string, user: User): boolean {
    if (user && user.organisation_type === 'ORG') {
      const prodCategorySnakeLC = this.prodConfig.getCategoryAbbreviationLC(productCategory);
      const prodTypeSnakeLC = this.prodConfig.getProductTypeAbbreviationSnakeLC(productType);
      const appliedForFlag = `is_credit_limit_applied_for_${prodCategorySnakeLC}_${prodTypeSnakeLC}`;
      const setForFlag = `is_credit_limit_set_for_${prodCategorySnakeLC}_${prodTypeSnakeLC}`;
      if (!user[appliedForFlag] && !user[setForFlag]) {
        return true;
      }
    }
    return false;
  }
}
